/**
 * Share-state permalinks — spec §F1 ("URL state (share/permalink): serialise
 * `{view, indicator, overlays, biv, level, year, country, layersOn, basemap}` into the hash
 * on 'Share'; hydrate store from hash on boot"), §F5.10 group 3 (share → clipboard +
 * toast) and §F5.11 ("share URL restores full map state on load").
 *
 * ## The scheme
 *
 * `#v=map&ind=b21&ov=c21,d2:%23C77B21&biv=c21&lv=1&y=2026&cum=1&c=BFA&ly=soc,chirps&bm=satellite&st=quantile.5.78.brown`
 *
 * One `key=value` pair per field, `&`-separated, **in a fixed order** (`KEY_ORDER` — the
 * spec's own list, then `st`), because a permalink is a string readers compare, mail to
 * each other and paste into tickets: two identical views must produce byte-identical URLs.
 * Keys are the mockup's (`v`, `ind`, `lv`, `y`, `c`), extended for the fields M3 added.
 *
 * Values are `encodeURIComponent`d *per component*, so the structural `,` and `:` stay
 * legible while a colour's `#` becomes `%23` (a bare `#` would truncate the hash).
 *
 * ## What is in it, and what is not
 *
 * §F1's nine fields, plus three documented extras (NOTES #315, #361):
 *
 * * **`st` (`indStyle`)** — §K M3's acceptance line is "permalink restores", and a shared
 *   map whose classification silently reverts to 5 quantile classes on the auto ramp is not
 *   the map that was shared. The reader chose those breaks; they travel.
 * * **`overlays[].color`** — §F5.6(c)'s recolour, for the same reason: §G4 re-picks a
 *   colour from the *fill* ramp, so an overlay the sender deliberately moved off the auto
 *   pick would come back somewhere else.
 * * **`cum` (`cumulative`)** — §F5.10's Cumulative checkbox. It is not styling: it changes
 *   what every number on the map *means* (the year's own reporting, or the total to that
 *   year), so a link that dropped it would restore a different map from the one shared.
 *   Emitted only when on, so every existing permalink stays byte-identical.
 * * **`aoi` (the drawn ring)** — added in M4, and **this reverses NOTES #315 on purpose**.
 *   M3 left the AOI out because §F5.10 handed AOIs to a dock that did not exist yet and the
 *   spec's own list excludes it. It exists now: §H2 and §H3 answer questions *about* the
 *   drawn polygon, and a link to an analysis view whose AOI is missing is not the view that
 *   was shared — it is the same map with the subject of the analysis removed. The **ring**
 *   travels, not the id: `lib/aoi.ts`'s hash names a polygon, it cannot rebuild one.
 *
 * Deliberately **out**: `aoiClip` (§G6's mask is a way of *looking* at the AOI, and the
 * recipient can raise it in one click from the AOI the link restores), `layerOpacity`,
 * `dockInd`, `selectedUnit`, `role` (the mockup put `role` in the hash; §I2 makes the
 * server the authority, so a shared link must never carry a privilege claim), and every
 * transient/UI flag (`domFilter`, `sidebarOpen`, …).
 *
 * ## Tolerance
 *
 * `parse` never throws and never half-applies: unknown keys are ignored, invalid values are
 * dropped individually, and a hash with nothing recognisable in it is `null`. Ids are the
 * one thing this module *cannot* validate — `/indicators` and `/layers` have not answered
 * when boot reads the hash — so applied ids are recorded in `pendingPermalinkIds()` and
 * `app/PermalinkSync.tsx` clears the unrecognised ones once the catalogues land.
 */
import type { StoreApi } from 'zustand';

import { BASEMAP_KEYS } from '../features/map/basemaps';
import { ringId, roundCoord } from './aoi';
import { shoelaceKm2 } from './geo';
import { isOverlayLevel } from './state';
import type { AppState, AppStore, Basemap, Country, ReportingLevel } from './state';

/* ------------------------------------------------------------------ views ⇄ routes */

/** The §F1 routes a permalink can name. `view` is the route path, not a store field. */
export type PermalinkView = 'map' | 'dashboard' | 'catalogue' | 'manager' | 'admin';

/** `v=` token → route path (`src/app/routes.tsx`). */
export const VIEW_PATHS: Record<PermalinkView, string> = {
  map: '/',
  dashboard: '/dashboard',
  catalogue: '/catalogue',
  manager: '/manager',
  admin: '/admin',
};

const VIEWS = Object.keys(VIEW_PATHS) as PermalinkView[];

/** Route path → `v=` token; `null` for anything unrouted (a 404 has no shareable view). */
export function viewForPath(pathname: string): PermalinkView | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return VIEWS.find((v) => VIEW_PATHS[v] === (path || '/')) ?? null;
}

/* ------------------------------------------------------------------ the payload */

/**
 * The permalink's own shape: §F1's list in store terms, every field optional because a
 * hash may carry any subset of them. `year` rather than `yearIdx` — an index into a
 * `years` array that could change length between deploys is not a durable thing to share.
 */
export interface PermalinkState {
  view?: PermalinkView;
  indicator?: string;
  overlays?: { id: string; color?: string }[];
  biv?: { on: boolean; y: string | null };
  reportingLevel?: ReportingLevel;
  year?: number;
  /** §F5.10 Cumulative — see the module note. Absent means the annual default. */
  cumulative?: boolean;
  country?: Country;
  /**
   * §F5.8's cascading filter, as the **unit id alone** (`sc=BFWS01`).
   *
   * The scope's type, admin level, country and name are all facts about the server's
   * hierarchy, and a hash is not allowed to assert one: `app/PermalinkSync` re-reads them
   * from `/hierarchy/units/{id}` and calls `setScope` with the answer. So a link cannot
   * smuggle in a scope that says a commune is a watershed, and it stays short.
   */
  scope?: string;
  /**
   * §F5.10's drawn ring as `lat:lon` vertices — see `MAX_AOI_VERTICES` and the module note.
   *
   * The ring alone: `areaKm2` is `lib/geo.ts`'s function of it and `id` is `lib/aoi.ts`'s,
   * so carrying either would be carrying a claim the recipient can check and therefore did
   * not need to be told. `applyToStore` recomputes both.
   */
  aoi?: [number, number][];
  layersOn?: string[];
  basemap?: Basemap;
  indStyle?: AppState['indStyle'];
}

/**
 * Serialisation order — fixed, so the same view always yields the same string.
 *
 * `sc` sits next to `c`: they are the two geography keys, and a reader comparing two URLs
 * should find the scope where they already look for the country. It is **appended to the
 * scheme, not inserted into it** in any way that matters — no link written before the
 * cascade existed carries an `sc`, so every one of them still serialises byte-identically.
 */
const KEY_ORDER = [
  'v', 'ind', 'ov', 'biv', 'lv', 'y', 'cum', 'c', 'sc', 'aoi', 'ly', 'bm', 'st',
] as const;

/** §F5.5's cap, re-applied on the way in: a hand-edited `ov=` cannot smuggle a third. */
const MAX_OVERLAYS = 2;

/**
 * How many vertices of a drawn AOI a link may carry.
 *
 * The store's ring has no cap and §E7's has 200 (`geometry.MAX_AOI_VERTICES`), but a
 * permalink is a *string people paste*, and a 200-vertex ring at 5 dp is ~3 400 characters
 * — past the point where mail clients wrap it and chat apps truncate it, which turns a
 * shared analysis into a link that half-restores. Sixty vertices is ~1 000 characters, and
 * it is far more than a mouse-drawn AOI has: `DrawAoi` puts down one vertex per click.
 *
 * Enforced on **both** sides, like `MAX_OVERLAYS`: `serialize` truncates and `parse`
 * refuses the rest, so a hand-edited hash cannot make the URL unbounded either.
 */
export const MAX_AOI_VERTICES = 60;

/** Fewer than three vertices is not a ring — `shoelaceKm2` answers 0 and nothing can be drawn. */
const MIN_AOI_VERTICES = 3;

/** §F5.6 "Classes select (3–7)". */
const MIN_CLASSES = 3;
const MAX_CLASSES = 7;

/** §F5.6 opacity slider bounds, as the integer percent the hash carries. */
const MIN_OPACITY_PCT = 20;
const MAX_OPACITY_PCT = 100;

const COUNTRIES: Country[] = ['ALL', 'BFA', 'MLI', 'NER', 'NGA'];

/**
 * What an id may look like. Indicator ids are `b21`/`c21`, layer ids `soc`/`chirps_dekad`;
 * the guard exists so a hand-written hash cannot push arbitrary text into a query key (it
 * would only 404, but a bounded charset keeps the failure in this module).
 */
const ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/** A CSS hex colour, the only form §F5.6(c)'s swatches and §G4's pool produce. */
const COLOR_RE = /^#[0-9A-Fa-f]{3,8}$/;

/** A plain decimal, the only form `aoiCoord` writes — no exponent, no sign but a leading `-`. */
const NUM_RE = /^-?\d{1,3}(\.\d{1,10})?$/;

/** `ramp: null` (§F5.6's AUTO chip) needs a token of its own — `st` is positional. */
const AUTO_RAMP = 'auto';

/**
 * One AOI coordinate on the wire: rounded to `lib/aoi.ts`'s five places, then written in
 * the shortest form that reads back as the same number.
 *
 * `String(12)` is `"12"`, not `"12.00000"` — three characters saved per vertex and a ring
 * a person can actually read. It is still byte-stable: JavaScript's number→string is the
 * shortest round-tripping decimal, which is a function of the value alone, and every value
 * here has been through `roundCoord` first. **The rounding is the same one `ringId` uses**,
 * so a restored AOI has the sender's id.
 */
function aoiCoord(value: number): string {
  return String(roundCoord(value));
}

/* ------------------------------------------------------------------ serialise */

/** Read the shareable subset out of the live store. `pathname` supplies `view`. */
export function fromAppState(state: AppState, pathname: string): PermalinkState {
  const view = viewForPath(pathname);
  return {
    ...(view ? { view } : {}),
    ...(state.indicator ? { indicator: state.indicator } : {}),
    ...(state.overlays.length ? { overlays: state.overlays } : {}),
    ...(state.biv.on ? { biv: state.biv } : {}),
    reportingLevel: state.reportingLevel,
    year: state.years[state.yearIdx],
    // Only when on: `cum=0` would say the same thing as its absence and lengthen every URL.
    ...(state.cumulative ? { cumulative: true } : {}),
    country: state.country,
    ...(state.scope ? { scope: state.scope.unitId } : {}),
    ...(state.aoi ? { aoi: state.aoi.pts } : {}),
    ...(state.layersOn.size ? { layersOn: [...state.layersOn] } : {}),
    basemap: state.basemap,
    indStyle: state.indStyle,
  };
}

/**
 * §F1 "serialise … into the hash". Returns the hash **including** its leading `#`, or the
 * empty string when there is nothing to say (so a caller can append it unconditionally).
 *
 * Absent/meaningless fields are omitted rather than emitted empty: `parse` then returns
 * exactly what was written, which is what makes `parse(serialize(s))` a round trip.
 */
export function serialize(state: PermalinkState): string {
  const parts: Partial<Record<(typeof KEY_ORDER)[number], string>> = {};

  if (state.view) parts.v = state.view;
  if (state.indicator) parts.ind = encodeURIComponent(state.indicator);
  if (state.overlays?.length) {
    parts.ov = state.overlays
      .slice(0, MAX_OVERLAYS)
      .map((o) => encodeURIComponent(o.id) + (o.color ? `:${encodeURIComponent(o.color)}` : ''))
      .join(',');
  }
  // `biv=1` is "bivariate on, no Y picked yet" — the state §F5.4 leaves the map in between
  // switching the mode on and clicking the second indicator.
  if (state.biv?.on) parts.biv = state.biv.y ? encodeURIComponent(state.biv.y) : '1';
  if (state.reportingLevel !== undefined) parts.lv = String(state.reportingLevel);
  if (state.year !== undefined) parts.y = String(state.year);
  if (state.cumulative) parts.cum = '1';
  if (state.country) parts.c = state.country;
  if (state.scope) parts.sc = encodeURIComponent(state.scope);
  // `lat:lon` per vertex, `,` between them — this module's own grammar (`,` separates
  // entries, `:` separates an entry's fields, exactly as `ov=` uses them). Nothing is
  // percent-encoded because nothing needs to be: digits, `-`, `.`, `,` and `:` are all
  // legal in a fragment, and a ring of `%2D`s would be unreadable for no gain.
  if (state.aoi?.length) {
    parts.aoi = state.aoi
      .slice(0, MAX_AOI_VERTICES)
      .map(([lat, lon]) => `${aoiCoord(lat)}:${aoiCoord(lon)}`)
      .join(',');
  }
  // **Sorted.** `layersOn` is a `Set` in the store, so `[...set]` is *insertion* order:
  // ticking `soc` then `chirps` and ticking `chirps` then `soc` describe the same map and
  // produced two different URLs. This module's contract (top of the file) is that two
  // identical views serialise byte-identically — a permalink is a string readers compare,
  // mail to each other and paste into tickets. Canonicalising here rather than in
  // `fromAppState` means *every* caller of `serialize` gets it, including a hand-built
  // `PermalinkState`. `parse` de-duplicates but preserves order, so a round trip is still
  // exact for any hash this function wrote.
  if (state.layersOn?.length) {
    parts.ly = [...state.layersOn].sort().map(encodeURIComponent).join(',');
  }
  if (state.basemap) parts.bm = state.basemap;
  if (state.indStyle) {
    const { method, k, opacity, ramp } = state.indStyle;
    parts.st = `${method}.${k}.${Math.round(opacity * 100)}.${ramp ?? AUTO_RAMP}`;
  }

  const body = KEY_ORDER.filter((k) => parts[k] !== undefined)
    .map((k) => `${k}=${parts[k]}`)
    .join('&');
  return body ? `#${body}` : '';
}

/* ------------------------------------------------------------------ parse */

function decode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null; // a stray `%` — drop the field, keep the rest of the hash
  }
}

function asInt(value: string): number | null {
  return /^-?\d+$/.test(value) ? Number(value) : null;
}

function parseOverlays(raw: string): PermalinkState['overlays'] | undefined {
  const out: { id: string; color?: string }[] = [];
  for (const entry of raw.split(',')) {
    if (out.length >= MAX_OVERLAYS) break;
    const sep = entry.indexOf(':');
    const idPart = sep === -1 ? entry : entry.slice(0, sep);
    const id = decode(idPart);
    if (!id || !ID_RE.test(id)) continue;
    if (out.some((o) => o.id === id)) continue; // `addOverlay` is idempotent; so is this
    const colorRaw = sep === -1 ? null : decode(entry.slice(sep + 1));
    // An unreadable colour loses the *override*, not the overlay: §G4 re-picks one.
    const color = colorRaw && COLOR_RE.test(colorRaw) ? colorRaw : undefined;
    out.push(color ? { id, color } : { id });
  }
  return out.length ? out : undefined;
}

/**
 * `aoi=lat:lon,lat:lon,…` → a ring, or `undefined`.
 *
 * **All or nothing, unlike every other field here.** The module's tolerance contract is
 * "each invalid value is dropped on its own", and for a list of ids that means dropping the
 * unreadable id and keeping the rest. A polygon is not a list: dropping one vertex of a ring
 * does not give a smaller ring, it gives a *different shape* — and a different shape is a
 * different analysis, presented to the recipient as the sender's. So a malformed vertex, a
 * coordinate off the globe, or fewer than three vertices drops the whole AOI, and the
 * recipient gets a map with no AOI rather than one with the wrong AOI.
 *
 * A ring longer than `MAX_AOI_VERTICES` is the one exception, and it is not a shape change:
 * it is a hash longer than this module writes, and it is **truncated** to the cap for the
 * same reason `ov=` stops at two — a hand-edited hash may not make the URL unbounded.
 */
function parseAoi(raw: string): [number, number][] | undefined {
  const pts: [number, number][] = [];
  for (const entry of raw.split(',')) {
    if (pts.length >= MAX_AOI_VERTICES) break;
    const sep = entry.indexOf(':');
    if (sep <= 0) return undefined;
    const lat = Number(entry.slice(0, sep));
    const lon = Number(entry.slice(sep + 1));
    // `Number('')` is 0 and `Number(' 1 ')` is 1 — neither is a coordinate this module
    // wrote, so the components are shape-checked before they are read as numbers.
    if (!NUM_RE.test(entry.slice(0, sep)) || !NUM_RE.test(entry.slice(sep + 1))) return undefined;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return undefined;
    pts.push([roundCoord(lat), roundCoord(lon)]);
  }
  return pts.length >= MIN_AOI_VERTICES ? pts : undefined;
}

function parseStyle(raw: string): AppState['indStyle'] | undefined {
  const parts = raw.split('.');
  // `>= 4`, not `=== 4`. `st` is positional and this module's tolerance contract is
  // "unknown keys are ignored, invalid values are dropped individually" — a fifth
  // component added by a *later* deploy is an unknown extra, not an invalid value, and
  // rejecting the whole field would silently drop the classification from a link shared
  // by a newer build. The four this version understands are read; the rest are ignored,
  // which is the same rule `parse` applies to the hash as a whole.
  if (parts.length < 4) return undefined;
  const [method, kRaw, opRaw, rampRaw] = parts as [string, string, string, string];
  if (method !== 'quantile' && method !== 'equal') return undefined;
  const k = asInt(kRaw);
  const pct = asInt(opRaw);
  if (k === null || k < MIN_CLASSES || k > MAX_CLASSES) return undefined;
  if (pct === null || pct < MIN_OPACITY_PCT || pct > MAX_OPACITY_PCT) return undefined;
  if (rampRaw !== AUTO_RAMP && !ID_RE.test(rampRaw)) return undefined;
  return { method, k, opacity: pct / 100, ramp: rampRaw === AUTO_RAMP ? null : rampRaw };
}

/**
 * §F1 "hydrate store from hash on boot", first half: hash → a validated `PermalinkState`.
 *
 * Tolerant by contract (§F5.11's "share URL restores" must never be a boot failure):
 * unknown keys are ignored, each invalid value is dropped on its own, and a hash carrying
 * nothing recognisable answers `null`. Ids are shape-checked only — see the module note.
 */
export function parse(hash: string): PermalinkState | null {
  // Accepts a bare body, a `#…` hash or a whole URL: every colour's `#` is `%23` by the
  // time it is written, so the first `#` is always the fragment's.
  const start = hash.indexOf('#');
  const body = start >= 0 ? hash.slice(start + 1) : hash;
  if (!body.trim()) return null;

  const raw = new Map<string, string>();
  for (const pair of body.split('&')) {
    const eq = pair.indexOf('=');
    if (eq <= 0) continue; // `#garbage`, `#=x`, `#&&` — nothing to read
    const key = pair.slice(0, eq);
    if (!raw.has(key)) raw.set(key, pair.slice(eq + 1)); // first wins, so a dup cannot flip state
  }

  const out: PermalinkState = {};

  const v = raw.get('v');
  if (v && (VIEWS as string[]).includes(v)) out.view = v as PermalinkView;

  const ind = raw.get('ind') ? decode(raw.get('ind')!) : null;
  if (ind && ID_RE.test(ind)) out.indicator = ind;

  const ov = raw.get('ov');
  if (ov) {
    const overlays = parseOverlays(ov);
    if (overlays) out.overlays = overlays;
  }

  // §F5.4: bivariate mode *is* "the base indicator as X plus a Y", so a `biv` with no `ind`
  // to be X describes nothing the map can draw and is dropped rather than half-applied.
  const bivRaw = raw.get('biv');
  if (bivRaw !== undefined && out.indicator) {
    if (bivRaw === '1') out.biv = { on: true, y: null };
    else {
      const y = decode(bivRaw);
      if (y && ID_RE.test(y)) out.biv = { on: true, y };
    }
  }

  // §M3.5 adds a non-numeric reporting mode, so `lv` is matched as a token first and only
  // then parsed as an admin level. Anything else is dropped, as before: a hand-edited hash
  // must not be able to put the store in a level the widget cannot represent.
  const lvRaw = raw.get('lv');
  if (isOverlayLevel(lvRaw)) out.reportingLevel = lvRaw;
  else if (lvRaw !== undefined) {
    const lv = asInt(lvRaw);
    if (lv === 0 || lv === 1 || lv === 2) out.reportingLevel = lv;
  }

  const y = raw.get('y') ? asInt(raw.get('y')!) : null;
  if (y !== null && y >= 1900 && y <= 2200) out.year = y;

  // Tolerant in the module's usual sense: only the exact `1` this module writes turns it
  // on. `cum=0`, `cum=true` and `cum=yes` are all *not* what `serialize` produces, and
  // guessing at a hand-edited value would be a way to switch a reader's map into a reading
  // they did not ask for.
  if (raw.get('cum') === '1') out.cumulative = true;

  const c = raw.get('c');
  if (c && (COUNTRIES as string[]).includes(c)) out.country = c as Country;

  // Shape only — the same rule as every other id here. Whether `BFWS01` is a watershed, or
  // exists at all, is `/hierarchy/units/{id}`'s answer and `PermalinkSync` asks for it.
  const sc = raw.get('sc') ? decode(raw.get('sc')!) : null;
  if (sc && ID_RE.test(sc)) out.scope = sc;

  const aoiRaw = raw.get('aoi');
  if (aoiRaw) {
    const aoi = parseAoi(aoiRaw);
    if (aoi) out.aoi = aoi;
  }

  const ly = raw.get('ly');
  if (ly) {
    const ids: string[] = [];
    for (const part of ly.split(',')) {
      const id = decode(part);
      if (id && ID_RE.test(id) && !ids.includes(id)) ids.push(id);
    }
    if (ids.length) out.layersOn = ids;
  }

  const bm = raw.get('bm');
  if (bm && (BASEMAP_KEYS as readonly string[]).includes(bm)) out.basemap = bm as Basemap;

  const st = raw.get('st');
  if (st) {
    const indStyle = parseStyle(st);
    if (indStyle) out.indStyle = indStyle;
  }

  // §F5.11 "bivariate excludes overlays and vice versa" — a hand-edited hash carrying both
  // resolves to bivariate, the mode the store's own `setMapMode` would leave standing.
  if (out.biv?.on) delete out.overlays;

  return Object.keys(out).length ? out : null;
}

/* ------------------------------------------------------------------ apply */

/** Ids a hash asked for that only the server can confirm — see `PermalinkSync`. */
export interface PermalinkIds {
  /** `indicator`, every overlay id and `biv.y`, in that order, deduplicated. */
  indicators: string[];
  layers: string[];
  /**
   * §F5.8 `sc=` — at most one org-unit id, and a list only so it shares the machinery the
   * other two kinds already have (`clearPendingPermalinkIds`, "fires nothing when empty").
   * Unlike them it is not merely *checked* against a catalogue: the store has no scope at
   * all until `/hierarchy/units/{id}` answers, because type and level cannot be guessed
   * from an id (`BF57` is a region, `NE005` is a region, `NE005009` is a commune).
   */
  scope: string[];
}

const NO_IDS: PermalinkIds = { indicators: [], layers: [], scope: [] };

/**
 * §F1 "hydrate store from hash", second half: a parsed permalink → the §F1 store.
 *
 * Written with `setState` rather than the store's actions on purpose — `setIndicator`
 * resets `indStyle.ramp` (§F5.2) and `setMapMode` empties `overlays`, both of which would
 * throw away the very fields the hash is restoring. Returns the ids the caller must have
 * confirmed against `/indicators` and `/layers` once those land.
 *
 * **`scope` is the one field this function does not apply.** Every other key is a value the
 * hash may simply state; a scope is a row in `org_units`, and its type and admin level
 * decide which polygons the map draws. So `sc=` is returned as pending and `PermalinkSync`
 * calls `setScope` once `/hierarchy/units/{id}` has said what the id actually is. The
 * restored `lv=` written here survives that: `setScope` keeps the wanted level whenever the
 * scope allows it (`levelWithinScope`).
 */
export function applyToStore(
  store: Pick<StoreApi<AppStore>, 'getState' | 'setState'>,
  parsed: PermalinkState,
): PermalinkIds {
  const current = store.getState();
  const patch: Partial<AppState> = {};

  if (parsed.indicator !== undefined) patch.indicator = parsed.indicator;
  if (parsed.overlays !== undefined) patch.overlays = parsed.overlays.map((o) => ({ ...o }));
  if (parsed.biv !== undefined) {
    patch.biv = { ...parsed.biv };
    patch.overlays = []; // §F5.11 mutual exclusion, belt and braces with `parse`
  }
  if (parsed.reportingLevel !== undefined) {
    patch.reportingLevel = parsed.reportingLevel;
    // §M3.5's fallback level rides along. `setReportingLevel` maintains `lastAdminLevel`
    // for exactly this reason — it is where the radio demotes to when the mapped indicator
    // turns out to have no watershed values — and this function writes with `setState`
    // rather than the actions (see the docstring), so it has to maintain it too. Without
    // this, restoring `lv=2` and then picking an indicator with no watershed stratum
    // dropped the reader to the store's default level 1, not to the level they shared.
    //
    // **Not when the link also carries a filter.** `lv=` is then not a level anyone chose
    // from the whole programme: `fromAppState` serialised whatever `setScope` had *forced*
    // (a region scope maps communes, so a shared region link always says `lv=2`). Writing
    // that into `lastAdminLevel` is writing the wrong answer to a different question — the
    // one "Clear filter" asks (`state.ts::clearScope`) — and it landed the reader on a
    // whole-country commune map, which is the exact outcome that field exists to prevent.
    // A link cannot state the sender's pre-filter level, so the recipient keeps the app's:
    // their session began filtered, and clearing it returns them to where a session starts.
    if (!isOverlayLevel(parsed.reportingLevel) && parsed.scope === undefined) {
      patch.lastAdminLevel = parsed.reportingLevel;
    }
  }
  if (parsed.cumulative !== undefined) patch.cumulative = parsed.cumulative;
  if (parsed.country !== undefined) patch.country = parsed.country;
  if (parsed.aoi !== undefined) {
    // The two derived fields are rebuilt here rather than carried: the area is `lib/geo.ts`'s
    // **corrected** shoelace (NOTES #308 — §G7's literal formula is wrong by ~258×), and the
    // id is `lib/aoi.ts`'s hash of the same rounding the hash was written at, so the
    // recipient's AOI is the sender's AOI by identity and not merely by coordinates.
    patch.aoi = { pts: parsed.aoi, areaKm2: shoelaceKm2(parsed.aoi), id: ringId(parsed.aoi) };
  }
  if (parsed.basemap !== undefined) patch.basemap = parsed.basemap;
  if (parsed.indStyle !== undefined) patch.indStyle = { ...parsed.indStyle };
  if (parsed.layersOn !== undefined) patch.layersOn = new Set(parsed.layersOn);
  if (parsed.year !== undefined) {
    // `years` is a client-side mirror of `/meta/config.years`: a year outside it is not an
    // error, it is a link from a deploy with a different window — keep the current index.
    const idx = current.years.indexOf(parsed.year);
    if (idx >= 0) patch.yearIdx = idx;
    // Pinned whether or not the year is in this deploy's window: the link *stated* a year,
    // so the catalogue's `default_year` must not overwrite it when `/indicators` lands.
    patch.yearPinned = true;
  }

  store.setState(patch);

  const indicators = [
    ...new Set(
      [
        parsed.indicator,
        ...(parsed.overlays ?? []).map((o) => o.id),
        parsed.biv?.y ?? undefined,
      ].filter((id): id is string => Boolean(id)),
    ),
  ];
  return {
    indicators,
    layers: parsed.layersOn ?? [],
    scope: parsed.scope ? [parsed.scope] : [],
  };
}

/* ------------------------------------------------------------------ boot */

let pendingIds: PermalinkIds = NO_IDS;

/** Ids hydrated from the hash and not yet confirmed (`app/PermalinkSync.tsx`). */
export function pendingPermalinkIds(): PermalinkIds {
  return pendingIds;
}

/** Called by `PermalinkSync` once a catalogue has answered for that half. */
export function clearPendingPermalinkIds(kind: keyof PermalinkIds): void {
  if (!pendingIds[kind].length) return;
  pendingIds = { ...pendingIds, [kind]: [] };
}

/** Test helper — a fresh module state between cases. */
export function resetPendingPermalinkIds(): void {
  pendingIds = NO_IDS;
}

/**
 * §F1 "hydrate store from hash on boot". Called from `main.tsx` **before** `createRoot`, so
 * the very first render — and therefore the first `['values', …]`/`['layers', …]` query —
 * already sees the shared state: hydrating from an effect would fire, and pay for, a whole
 * round of queries against the defaults first.
 *
 * If the hash names a different view from the one being loaded, the path is corrected with
 * `replaceState` (before React Router reads `window.location`), never `pushState`: a
 * restore is not a navigation the Back button should have to undo.
 */
export function hydrateFromHash(
  store: Pick<StoreApi<AppStore>, 'getState' | 'setState'>,
  loc: Pick<Location, 'hash' | 'pathname' | 'search'> = window.location,
  history: Pick<History, 'replaceState'> = window.history,
): PermalinkState | null {
  const parsed = parse(loc.hash);
  if (!parsed) return null;

  pendingIds = applyToStore(store, parsed);

  if (parsed.view) {
    const path = VIEW_PATHS[parsed.view];
    if (path !== loc.pathname) history.replaceState(null, '', `${path}${loc.search}${loc.hash}`);
  }
  return parsed;
}

/* ------------------------------------------------------------------ share */

/** The hash for the live store — what Share writes into the address bar. */
export function shareHash(state: AppState, pathname: string = window.location.pathname): string {
  return serialize(fromAppState(state, pathname));
}

/**
 * §F5.10 group 3: the absolute URL the Share button copies —
 * `origin + pathname + hash`, with the **query string dropped**. `?demo=1` is the §F3
 * role switcher, a developer affordance and not part of the view; §I2 makes the server the
 * authority on privilege, so a link that carries one is at best noise and at worst reads
 * as a claim. (The address bar keeps its query — see `MapTools.shareView`.)
 */
export function shareUrl(
  state: AppState,
  loc: Pick<Location, 'origin' | 'pathname'> = window.location,
): string {
  return `${loc.origin}${loc.pathname}${shareHash(state, loc.pathname)}`;
}
